import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { api } from "../lib/api";
import { loadAnalytics, trackPageView, clearAnalyticsCookies, analyticsLoaded } from "../lib/analytics";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "costock_cookie_consent";
// Fired by every "Cookie settings" link. Withdrawing consent has to be as easy as
// giving it (DSGVO Art. 7(3)), so the banner can be reopened from any page.
const OPEN_EVENT = "costock:cookie-settings";

/** Read stored consent. Shape: { granted:{...}, version, consent_id } */
export function readConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}
export const hasConsent = (category) => !!readConsent()?.granted?.[category];

/** "Cookie settings" — reopens the banner with the current choice filled in. */
export function CookieSettingsLink({ className = "" }) {
  const { t } = useTranslation();
  return (
    <button type="button" data-testid="cookie-settings-link" className={className}
            onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}>
      {t("cookies.settings")}
    </button>
  );
}

/**
 * The consent banner, and the only place analytics is ever switched on.
 *
 * Under § 25(1) TDDDG and the DSGVO, statistics need prior opt-in: nothing from
 * Google is loaded until "Statistik" is accepted (see lib/analytics.js). Rejecting
 * is one click, the same as accepting. The choice is stored in the browser and, as
 * evidence, on the server (DSGVO Art. 7(1)), and is asked again whenever the policy
 * version changes.
 */
export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const ids = (c) => ({ gtmId: c?.gtm_container_id, gaId: c?.google_analytics_id });

  useEffect(() => {
    api.cookieConfig().then((r) => {
      setConfig(r.data);
      const stored = readConsent();
      // Re-ask when the policy version changes — old consent doesn't cover new terms.
      if (!stored || String(stored.version) !== String(r.data.version)) setOpen(true);
      else if (stored.granted?.analytics) {
        loadAnalytics(ids(r.data));
        trackPageView(window.location.pathname);
      }
    }).catch(() => {});
  }, []);

  // Reopened from a "Cookie settings" link: show the detail view with what is stored.
  useEffect(() => {
    const reopen = () => {
      setAnalytics(hasConsent("analytics"));
      setDetails(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  // One page view per route change. A no-op until the tags are loaded.
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);

  const persist = useCallback((granted) => {
    const previous = readConsent();
    const consentId = previous?.consent_id || crypto.randomUUID();
    const record = { granted, version: config?.version || "1", consent_id: consentId };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    // Server-side copy: DSGVO Art. 7(1) puts the burden of proving consent on us.
    api.recordConsent({ granted, policy_version: record.version, consent_id: consentId }).catch(() => {});
    setOpen(false);
    setDetails(false);
    if (granted.analytics) {
      const first = !analyticsLoaded();
      loadAnalytics(ids(config));
      if (first) trackPageView(window.location.pathname);
    } else if (previous?.granted?.analytics || analyticsLoaded()) {
      // Withdrawn. A loaded container cannot be unloaded, so the cookies go and the
      // page reloads without it.
      clearAnalyticsCookies();
      window.location.reload();
    }
  }, [config]);

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4" data-testid="cookie-banner"
         role="dialog" aria-modal="false" aria-labelledby="cookie-banner-title">
      <div className="mx-auto max-w-3xl rounded-2xl bg-white shadow-xl border border-slate-200 p-4 sm:p-5 max-h-[85vh] overflow-y-auto">
        <div className="flex gap-3">
          <Cookie size={20} className="text-brand-500 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h2 id="cookie-banner-title" className="font-heading font-semibold tracking-tight">{t("cookies.title")}</h2>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{t("cookies.body")}</p>

            {details && (
              <div className="mt-3 space-y-2" data-testid="cookie-details">
                <label className="flex items-start gap-2.5 opacity-70">
                  <input type="checkbox" checked disabled className="mt-1" />
                  <span className="text-sm">
                    <span className="font-medium text-slate-800">{t("cookies.necessary")}</span>
                    <span className="block text-xs text-slate-500">{t("cookies.necessaryDesc")}</span>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="cookie-analytics"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-800">{t("cookies.analytics")}</span>
                    <span className="block text-xs text-slate-500">{t("cookies.analyticsDesc")}</span>
                  </span>
                </label>
              </div>
            )}

            {/* Accept and reject sit side by side, same size, same weight of click —
                a reject that takes more effort than accept is not a free choice. */}
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                data-testid="cookie-accept-all"
                className="btn-primary justify-center flex-1"
                onClick={() => persist({ necessary: true, analytics: true })}
              >
                {t("cookies.acceptAll")}
              </button>
              <button
                data-testid="cookie-reject"
                className="btn-outline justify-center flex-1"
                onClick={() => persist({ necessary: true, analytics: false })}
              >
                {t("cookies.rejectAll")}
              </button>
              {details ? (
                <button
                  data-testid="cookie-save"
                  className="btn-ghost justify-center flex-1"
                  onClick={() => persist({ necessary: true, analytics })}
                >
                  {t("cookies.saveChoice")}
                </button>
              ) : (
                <button data-testid="cookie-customise" className="btn-ghost justify-center flex-1"
                        onClick={() => { setAnalytics(hasConsent("analytics")); setDetails(true); }}>
                  {t("cookies.customise")}
                </button>
              )}
            </div>

            <div className="flex gap-4 mt-3 text-xs text-slate-400">
              {/* The privacy policy is where the cookies are described — § 25 TDDDG
                  wants the information reachable from the banner, not on a page of
                  its own. */}
              <Link to="/privacy" className="hover:text-brand-600">{t("legal.privacy")}</Link>
              <Link to="/imprint" className="hover:text-brand-600">{t("legal.imprint")}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
